const TRUSTED_HOST = 'sytex.io';
const TRUSTED_HOST_SUFFIX = '.sytex.io';

/**
 * Returns true only when `value` is an absolute http(s) URL whose host is
 * `sytex.io` or any of its subdomains (`*.sytex.io`). Every Sytex property
 * across all environments (dev, prod and EU prod) lives under `sytex.io`, so
 * this is the complete trust boundary for post-login redirect targets.
 *
 * Used to prevent open redirects: relative URLs, protocol-relative URLs
 * (`//evil.com`), non-http(s) schemes (`javascript:`, `data:`) and any
 * external host are rejected. The WHATWG `URL` parser also neutralises the
 * classic bypasses, e.g. `https://app.sytex.io@evil.com` resolves to host
 * `evil.com`, and `https://sytex.io.evil.com` fails the suffix check.
 */
export function isTrustedRedirectUrl(value: string | null | undefined): boolean {
  if (!value) {
    return false;
  }

  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return false;
  }

  if (url.protocol !== 'https:' && url.protocol !== 'http:') {
    return false;
  }

  const host = url.hostname.toLowerCase();
  return host === TRUSTED_HOST || host.endsWith(TRUSTED_HOST_SUFFIX);
}

const CLI_CALLBACK_HOST = '127.0.0.1';
const CLI_CALLBACK_PATH = '/callback';
const MIN_CLI_CALLBACK_PORT = 1024;
const MAX_CLI_CALLBACK_PORT = 65535;
const CLI_ROUTE_PATTERN = /^(\/[A-Za-z-]+)?\/cli\/?$/;

/**
 * True for the port an assistant's CLI listens on for its login callback: a
 * plain decimal number in the unprivileged range. Anything else (signs,
 * hex, whitespace, privileged ports) is refused before it reaches a URL.
 */
export function isValidCliCallbackPort(port?: string | null): boolean {
  if (!port || !/^\d{1,5}$/.test(port)) {
    return false;
  }
  const portNumber = Number(port);
  return portNumber >= MIN_CLI_CALLBACK_PORT && portNumber <= MAX_CLI_CALLBACK_PORT;
}

/**
 * The only loopback target this app ever navigates to, and only from the /cli
 * route: `http://127.0.0.1:<port>/callback`. It is deliberately NOT part of
 * {@link isTrustedRedirectUrl} — the general post-login redirect must keep
 * refusing every non-sytex.io host. `localhost` is refused too: it can resolve
 * to something other than this machine's IPv4 loopback.
 */
export function isTrustedCliCallbackUrl(value?: string | null): boolean {
  if (!value) {
    return false;
  }

  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return false;
  }

  const hasCredentials = url.username !== '' || url.password !== '';
  const isLoopbackCallback =
    url.protocol === 'http:' && url.hostname === CLI_CALLBACK_HOST && url.pathname === CLI_CALLBACK_PATH;
  return isLoopbackCallback && !hasCredentials && isValidCliCallbackPort(url.port);
}

/** `url` without its query and fragment, for reports that must not carry them. */
export function urlWithoutQuery(url: string): string {
  const parsed = new URL(url);
  return `${parsed.origin}${parsed.pathname}`;
}

/**
 * Builds the CLI callback URL carrying `query`, or null when the port is not
 * one {@link isTrustedCliCallbackUrl} accepts.
 */
export function cliCallbackUrl(port: string, query: Record<string, string>): string | null {
  if (!isValidCliCallbackPort(port)) {
    return null;
  }
  const url = new URL(`http://${CLI_CALLBACK_HOST}:${port}${CLI_CALLBACK_PATH}`);
  Object.entries(query).forEach(([key, value]) => url.searchParams.set(key, value));
  const href = url.toString();
  return isTrustedCliCallbackUrl(href) ? href : null;
}

/** True when `value` is this app's own /cli page (any locale prefix) on `origin`. */
export function isCliReturnUrl(value: string | null, origin: string): boolean {
  if (!value) {
    return false;
  }

  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return false;
  }

  return url.origin === origin && CLI_ROUTE_PATTERN.test(url.pathname);
}
